import { createContext, useContext, useState, useEffect } from "react";
import dayjs from "dayjs";

import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { useGetPendingTransactions } from "../../../queries/useGetPendingTransactions";
import { useGetProcessedTransactions } from "../../../queries/useGetProcessedTransactions";
import { useGetUserData } from "../../../queries/useGetUserData";
import { useGetTransactionDetails } from "../../../queries/useGetTransactionDetails";
import { useApprovePendingTransaction } from "../../../queries/useApprovePendingTransaction";
import { useDeclinePendingTransaction } from "../../../queries/useDeclinePendingTransaction";
import { useGetWithdrawalRules } from "../../../queries/useGetWithdrawalRules";
import { useCreateWithdrawalRule } from "../../../queries/useCreateWithdrawalRule";
import { useUpdateWithdrawalRule } from "../../../queries/useUpdateWithdrawalRule";
import { useDeactivateWithdrawalRule } from "../../../queries/useDeactivateWithdrawalRule";
import { useActivateWithdrawalRule } from "../../../queries/useActivateWithdrawalRule";
import { useDeleteWithdrawalRule } from "../../../queries/useDeleteWithdrawalRule";

const WithdrawalReviewContext = createContext(null);

export const WithdrawalReviewProvider = ({ children }) => {
  const dateRange = [dayjs().subtract(6, "day"), dayjs()];
  const [rangeDates, setRangeDates] = useState(dateRange);
  const [selectedTab, setSelectedTab] = useState("PENDING");

  const [pendingPageSize, setPendingPageSize] = useState(100);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingTotalCount, setPendingTotalCount] = useState(null);
  const [pendingLastKey, setPendingLastKey] = useState(null);

  const [processedPageSize, setProcessedPageSize] = useState(100);
  const [processedCurrentPage, setProcessedCurrentPage] = useState(1);
  const [processedTotalCount, setProcessedTotalCount] = useState(null);
  const [processedLastKey, setProcessedLastKey] = useState(null);

  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [recordDeclined, setRecordDeclined] = useState(null);
  const [userDetails, setUserDetails] = useState(null);

  const {
    data: pendingTransactions,
    refetch: refetchPending,
    isLoading: loadingPending,
  } = useGetPendingTransactions(
    rangeDates[0]?.format("YYYY-MM-DD"),
    rangeDates[1]?.format("YYYY-MM-DD"),
    pendingPageSize,
    pendingCurrentPage,
    pendingLastKey
  );

  const {
    data: processedTransactions,
    refetch: refetchProcessed,
    isLoading: loadingProcessed,
  } = useGetProcessedTransactions(
    rangeDates[0]?.format("YYYY-MM-DD"),
    rangeDates[1]?.format("YYYY-MM-DD"),
    processedPageSize,
    processedCurrentPage,
    processedLastKey
  );

  const {
    data: withdrawalRules,
    refetch: refetchRules,
    isLoading: loadingRules,
  } = useGetWithdrawalRules();

  const { mutateAsync: getUserData } = useGetUserData();
  const { mutateAsync: getTransactionDetails } = useGetTransactionDetails();
  const { mutateAsync: approvePendingTransaction } =
    useApprovePendingTransaction();
  const { mutateAsync: declineTransaction } = useDeclinePendingTransaction();
  const { mutateAsync: createWithdrawalRule } = useCreateWithdrawalRule();
  const { mutateAsync: updateWithdrawalRule } = useUpdateWithdrawalRule();
  const { mutateAsync: deactivateWithdrawalRule } =
    useDeactivateWithdrawalRule();
  const { mutateAsync: activateWithdrawalRule } = useActivateWithdrawalRule();
  const { mutateAsync: deleteWithdrawalRule } = useDeleteWithdrawalRule();

  useEffect(() => {
    if (pendingTransactions?.last_evaluated_key) {
      if (pendingPageSize === 1) {
        setPendingLastKey(null);
      } else {
        setPendingLastKey(pendingTransactions.last_evaluated_key);
      }
    }

    if (pendingTransactions?.pagination?.total_results) {
      setPendingTotalCount(pendingTransactions?.pagination?.total_results);
    }
  }, [pendingTransactions, pendingPageSize]);

  useEffect(() => {
    if (processedTransactions?.last_evaluated_key) {
      if (processedPageSize === 1) {
        setProcessedLastKey(null);
      } else {
        setProcessedLastKey(processedTransactions.last_evaluated_key);
      }
    }

    if (processedTransactions?.pagination?.total_results) {
      setProcessedTotalCount(processedTransactions?.pagination?.total_results);
    }
  }, [processedTransactions, processedPageSize]);

  const handleViewUser = async ({ user_id }) => {
    const result = await getUserData(user_id);
    localStorage.setItem("selected_user_data", JSON.stringify(result));
    localStorage.setItem("wallet_active", "false");
    window.open(NAVIGATION_ROUTES.USER_MANAGER, "_blank");
  };

  const handleApproved = async ({ user_id, transaction_id }) => {
    await approvePendingTransaction({ user_id, transaction_id });
    refetchPending();
    refetchProcessed();
  };

  const handleDecline = async ({ user_id, transaction_id, reason }) => {
    await declineTransaction({ user_id, transaction_id, reason });
    refetchPending();
    refetchProcessed();
    setIsDeclineModalOpen(false);
  };

  const openDeclinedModal = (record) => {
    setRecordDeclined(record);
    setIsDeclineModalOpen(true);
  };

  const openDetailsModal = async ({ user_id, transaction_id, ...rest }) => {
    const result = await getTransactionDetails({ user_id, transaction_id });
    setUserDetails({ ...result, user_id, transaction_id, ...rest });
    setIsDetailsModalOpen(true);
  };

  const handleCreateRule = async (rule) => {
    await createWithdrawalRule(rule);
    refetchRules();
  };

  const handleUpdateRule = async (rule) => {
    await updateWithdrawalRule(rule);
    refetchRules();
  };

  const handleDeactivateRule = async (rule_id) => {
    await deactivateWithdrawalRule({ rule_id });
    refetchRules();
  };

  const handleActivateRule = async (rule_id) => {
    await activateWithdrawalRule({ rule_id });
    refetchRules();
  };

  const handleDeleteRule = async (rule_id) => {
    await deleteWithdrawalRule({ rule_id });
    refetchRules();
  };

  return (
    <WithdrawalReviewContext.Provider
      value={{
        rangeDates,
        setRangeDates,

        selectedTab,
        setSelectedTab,

        pendingTransactions,
        processedTransactions,
        withdrawalRules,
        loadingPending,
        loadingProcessed,
        loadingRules,

        pendingPageSize,
        setPendingPageSize,
        pendingCurrentPage,
        setPendingCurrentPage,
        pendingTotalCount,

        processedPageSize,
        setProcessedPageSize,
        processedCurrentPage,
        setProcessedCurrentPage,
        processedTotalCount,

        isDeclineModalOpen,
        setIsDeclineModalOpen,
        isDetailsModalOpen,
        setIsDetailsModalOpen,
        recordDeclined,
        userDetails,

        handleViewUser,
        handleApproved,
        handleDecline,
        openDeclinedModal,
        openDetailsModal,
        handleCreateRule,
        handleUpdateRule,
        handleDeactivateRule,
        handleActivateRule,
        handleDeleteRule,
      }}
    >
      {children}
    </WithdrawalReviewContext.Provider>
  );
};

export const useWithdrawalReview = () => useContext(WithdrawalReviewContext);
